/* eslint-disable sort-keys */

import { Icon } from '@clientbase/clientbase-library'
import { Box, Tooltip } from '@mui/material'
import { BalanceDetailItem } from 'models/BalanceDetails'
import moment from 'moment'

import { ButtonRounded } from 'components/button'
import { TableNameWrapper, TableNickname } from 'components/Table/Table.styles'

import { formatMoney } from 'utils/FormatMoney'

import { theme } from 'styles/theme'

import * as S from './ModalBalance.styles'

type ConvertToRowsParams = {
  records: BalanceDetailItem[] | null
  handleOpenExpenseGroup?: (record: BalanceDetailItem) => void
  handleOpenDetailInvoice: (uuid: string) => void
  handleOpenExpenseDetail: (uuid: string) => void
}

const formatDate = (date: string) => {
  if (date)
    return moment(date.slice(0, 10), 'YYYY-MM-DD')
      .format('DD/MM/YYYY')
      .toString()
  return
}

type CreateDataTable = {
  description: string
  type: 'Receivable' | 'Expense'
  dueDate: string
  paidDate: string
  amountPaid: string
  amount: string
  uuid: string
  handleOpenDetailInvoice: (uuid: string) => void
  handleOpenExpenseDetail: (uuid: string) => void
  handleOpenExpenseGroup?: (record: BalanceDetailItem) => void
  installments?: number
  paymentType: string
  nickname?: string
  /** Linha agrupada (SC-10840): abre a lista dos itens, não o detalhe de um só. */
  record?: BalanceDetailItem
}

export const createDataTable = ({
  description,
  dueDate,
  paidDate,
  amountPaid,
  amount,
  uuid,
  handleOpenDetailInvoice,
  handleOpenExpenseDetail,
  type,
  installments,
  paymentType,
  nickname,
  record,
  handleOpenExpenseGroup
}: CreateDataTable) => {
  const paymentTransform = {
    pix: 'Pix',
    boleto: 'Boleto',
    boleto_pix: 'Pix',
    credit_card: 'Cartão de crédito',
    credit_card_installments: 'Cartão de crédito',
    credit_card_recurring: 'Cartão de crédito'
  }

  const fee = Number(amountPaid) - Number(amount) ?? 0

  let payment_type = paymentTransform[paymentType]
  payment_type =
    paymentType.includes('credit_card') && Number(installments ?? 0) > 1
      ? payment_type + ' - Parcela ' + installments
      : payment_type

  const handleRowAction = () => {
    if (type === 'Receivable') {
      handleOpenDetailInvoice(uuid)
      return
    }

    // Linha agrupada abre a LISTA dos itens; linha simples vai direto ao detalhe.
    if (record?.grouped && handleOpenExpenseGroup) {
      handleOpenExpenseGroup(record)
      return
    }

    handleOpenExpenseDetail(uuid)
  }

  return {
    uuid,
    clickable: true,
    type: (
      <Tooltip
        enterTouchDelay={0}
        title={type === 'Receivable' ? 'Crédito' : 'Débito'}
      >
        <S.ItemWrapper type={type}>
          <S.Item>{type === 'Receivable' ? '+' : '-'}</S.Item>
        </S.ItemWrapper>
      </Tooltip>
    ),
    description: (
      <TableNameWrapper>
        <p>{description}</p>
        {nickname && <TableNickname>{nickname}</TableNickname>}
      </TableNameWrapper>
    ),
    // Linha de grupo vem sem data: o grupo cobre meses, e estampar a data da expense mais
    // recente diria que tudo aconteceu naquele dia. O traço segue o padrão das outras colunas
    // vazias da tabela (tarifa e forma de pagamento em expense).
    dueDate: formatDate(dueDate) ?? '-',
    paidDate: formatDate(paidDate) ?? '-',
    amountPaid: formatMoney(amountPaid),
    fee: type == 'Receivable' ? formatMoney(fee) : '-',
    amountLiquid: formatMoney(amount),
    paymentType: type == 'Receivable' ? payment_type : '-',
    actions: (
      <Tooltip
        enterTouchDelay={0}
        arrow={true}
        placement="top"
        title={type === 'Receivable' ? 'Detalhes cobrança' : 'Detalhes despesa'}
      >
        <Box>
          <ButtonRounded onClick={handleRowAction} variant="transparent">
            <Icon icon="settings" sx={{ color: theme.palette.neutral[200] }} />
          </ButtonRounded>
        </Box>
      </Tooltip>
    )
  }
}

export const convertToRows = ({
  records,
  handleOpenDetailInvoice,
  handleOpenExpenseDetail,
  handleOpenExpenseGroup
}: ConvertToRowsParams) =>
  records?.map((record) => {
    if (!record) return {}
    return createDataTable({
      uuid: record?.referenceable_uuid,
      type: record.type,
      description: record.description,
      dueDate: record.due_date,
      paidDate: record.date_paid,
      amountPaid: record.amount_paid,
      amount: record.amount,
      handleOpenDetailInvoice,
      handleOpenExpenseDetail,
      installments: record.installments,
      paymentType: record.payment_type,
      nickname: record.nickname,
      record,
      handleOpenExpenseGroup
    })
  })
